/**
 * Greedy lane stacking (features.md §2): no manual thematic swimlanes, entries
 * simply fall into the first lane that is free.
 *
 * Periods are stacked once for the whole dataset rather than per band, because
 * a period must keep the same lane on both sides of a band break — that is what
 * makes a cut bar read as one continuous thing.
 *
 * Automatic stacking stays the rule; an entry pinned to a lane by hand claims
 * it first and the rest packs around it (features.md §5).
 */

export interface LaneOptions<T> {
  extent: (item: T) => [start: number, end: number];
  /** Stable tiebreak, so the same document always produces the same chart. */
  key: (item: T) => string;
  /** A lane chosen by the user, honoured as-is. */
  pinned?: (item: T) => number | undefined;
}

export function assignLanes<T>(items: readonly T[], options: LaneOptions<T>): Map<T, number> {
  const sorted = [...items].sort((a, b) => {
    const [aStart, aEnd] = options.extent(a);
    const [bStart, bEnd] = options.extent(b);
    if (aStart !== bStart) return aStart - bStart;
    // Longest first: a long bar laid down early leaves tidier gaps behind it.
    if (aEnd !== bEnd) return bEnd - aEnd;
    return options.key(a).localeCompare(options.key(b));
  });

  const occupied = new Map<number, Array<[number, number]>>();
  const lanes = new Map<T, number>();

  const isFree = (lane: number, start: number, end: number): boolean =>
    (occupied.get(lane) ?? []).every(([from, to]) => end <= from || start >= to);

  const claim = (lane: number, start: number, end: number): void => {
    const spans = occupied.get(lane);
    if (spans) spans.push([start, end]);
    else occupied.set(lane, [[start, end]]);
  };

  // Pinned entries first, and unconditionally: the user asked for that row.
  for (const item of sorted) {
    const lane = options.pinned?.(item);
    if (lane === undefined) continue;
    const [start, end] = options.extent(item);
    claim(lane, start, end);
    lanes.set(item, lane);
  }

  for (const item of sorted) {
    if (lanes.has(item)) continue;
    const [start, end] = options.extent(item);

    let lane = 0;
    while (!isFree(lane, start, end)) lane += 1;

    claim(lane, start, end);
    lanes.set(item, lane);
  }

  return lanes;
}

export function laneCount(lanes: ReadonlyMap<unknown, number>): number {
  let max = -1;
  for (const lane of lanes.values()) max = Math.max(max, lane);
  return max + 1;
}
